/* eslint-disable max-lines */
import React, { ReactNode } from 'react'
import { lazyRetry } from './utils/reactLazyRetryUtils'

export type route = {
  component?: ReactNode
  exact?: boolean
  name?: string
  path?: string
  routes?: route[]
}

// examples
const Dashboard = React.lazy(() =>
  lazyRetry(() => import('./pages/Dashboard/Dashboard'), 'dashboard'),
)
const UserRolesAndPermissions = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Settings/UserRolesConfiguration/UserRolesConfiguration'),
    'userRolesAndPermissions',
  ),
)
const MyProfile = React.lazy(() =>
  lazyRetry(
    () => import('./pages/MyProfile/ProfileLandingPage/Profile'),
    'MyProfile',
  ),
)
const CertificatesList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/CertificatesList/CertificatesList'),
    'CertificatesList',
  ),
)
const CertificateTypeList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeDirectory/CertificatesList/AddCertificateType/CertificateType'
      ),
    'CertificateTypeList',
  ),
)
const EmployeeList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/EmployeesList'),
    'EmployeeList',
  ),
)
const EmployeeReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/EmployeeReport/EmployeeReport'),
    'EmployeeReport',
  ),
)
const EmpDesignationReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeDirectory/EmployeeReport/EmployeeDesignationReport/EmployeeDesignationReport'
      ),
    'EmpDesignationReport',
  ),
)
const VisaDetailsList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/VisaList/VisaList'),
    'VisaDetailsList',
  ),
)
const EmployeeHandbook = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeHandbook/EmployeeHandbook'),
    'EmployeeHandbook',
  ),
)
const Handbook = React.lazy(() =>
  lazyRetry(() => import('./pages/EmployeeHandbook/ShowHandbook'), 'Handbook'),
)
const AttendanceReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/TimeAndAttendance/AttendanceReport/AttendanceReport'),
    'AttendanceReport',
  ),
)
const ShortfallTrackerReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/TimeAndAttendance/ShortfallTrackerReport/ShortfallTrackerReport'
      ),
    'ShortfallTrackerReport',
  ),
)
const EmployeeHandbookSettings = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeHandbook/HandbookSettings/EmployeeHandbookSettings'
      ),
    'EmployeeHandbookSettings',
  ),
)
const TimeInOfficeReport = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/TimeAndAttendance/TimeInOfficeReport/TimeInOfficeReport'),
    'TimeInOfficeReport',
  ),
)
const VpnAttendance = React.lazy(() =>
  lazyRetry(
    () => import('./pages/TimeAndAttendance/VpnAttendance/VpnAttendance'),
    'VpnAttendance',
  ),
)
const LeaveSettings = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/LeaveSettings/EmployeeLeaveSettings'),
    'LeaveSettings',
  ),
)
const LeaveReports = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Leaves/LeaveReports/LeaveReport'),
    'LeaveReports',
  ),
)

const MailConfiguration = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/MailConfiguration/EmailConfigTemplate'),
    'MailConfiguration',
  ),
)
const AddNewEmployee = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/EmployeesList/AddNewEmployee'),
    'AddNewEmployee',
  ),
)
const HiveActivityReport = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/TimeAndAttendance/HiveActivityReport/HiveActivityReport'),
    'HiveActivityReport',
  ),
)
const AddNewTemplate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Settings/MailConfiguration/AddTemplate/AddNewMailTemplate'
      ),
    'AddNewTemplate',
  ),
)

const ApplyLeave = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Leaves/ApplyLeave/EmployeeApplyLeave'),
    'ApplyLeave',
  ),
)

const EditEmployee = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/EmployeesList/EditEmployee'),
    'EditEmployee',
  ),
)
const SupportReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Support/Report/TicketReport'),
    'SupportReport',
  ),
)
const ScheduledInterview = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/ScheduledInterviews/ScheduledInterviews'),
    'ScheduledInterview',
  ),
)
const MyAttendance = React.lazy(() =>
  lazyRetry(
    () => import('./pages/TimeAndAttendance/MyAttendance/MyAttendance'),
    'MyAttendance',
  ),
)
const Clients = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/Clients/Clients'),
    'Clients',
  ),
)

const MyTickets = React.lazy(() =>
  lazyRetry(() => import('./pages/Support/MyTickets/MyTickets'), 'MyTickets'),
)
const LeaveSummary = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Leaves/LeaveSummary/LeaveSummaryLandingScreen/EmployeeLeaveSummary'
      ),
    'LeaveSummary',
  ),
)
const AddProject = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/AddEditProject/AddProject/AddProject'
      ),
    'AddProject',
  ),
)
const EditProject = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/AddEditProject/EditProject/EditProject'
      ),
    'EditProject',
  ),
)
const ProjectReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/Project/ProjectReport'),
    'ProjectReport',
  ),
)
const EditClient = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/Clients/EditClient/EditClient'),
    'EditClient',
  ),
)

const AddClient = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/Clients/AddClient/AddNewClient'),
    'AddClient',
  ),
)
const CreateNewTicket = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Support/RaiseTicket/CreateNewTicket'),
    'CreateNewTicket',
  ),
)
const ViewClient = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Clients/ClientInformation/ViewClientInformation'
      ),
    'ViewClient',
  ),
)
const TicketApprovals = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Support/TicketApprovals/TicketApprovals'),
    'TicketApprovals',
  ),
)

const BirthdaysListTable = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Dashboard/Birthdays/ListOfBirthdays'),
    'BirthdaysListTable',
  ),
)
const HolidaysList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Dashboard/Holidays/HolidaysList/ListOfHolidays'),
    'HolidaysList',
  ),
)
const AddHoliday = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Dashboard/Holidays/HolidaysList/AddHoliday/AddHoliday'),
    'AddHoliday',
  ),
)
const ProbationaryList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Dashboard/ProbationaryList/ProbationaryList'),
    'ProbationaryList',
  ),
)
const OnContractList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Dashboard/OnContract/OnContractList'),
    'OnContractList',
  ),
)
const UpdateTicket = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Support/TicketApprovals/UpdateTicket/UpdateTicket'),
    'UpdateTicket',
  ),
)
const UpdateTicketUnderTicketList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Support/MyTickets/UpdateTicket/UpdateTicket'),
    'UpdateTicketUnderTicketList',
  ),
)
const BookingList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/BookingList/BookingList'),
    'BookingList',
  ),
)
const EventTypeList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ConferenceRoomBooking/NewEvent/EventTypeList/EventTypeList'
      ),
    'EventTypeList',
  ),
)
const EmployeeAllocationReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/EmployeeAllocation/EmployeeAllocationReport'
      ),
    'EmployeeAllocationReport',
  ),
)
const EventsList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/EventList/EventList'),
    'EventsList',
  ),
)
const FeedbackForms = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ConferenceRoomBooking/EventList/FeedbackForms/FeedbackFormsList'
      ),
    'FeedbackForms',
  ),
)
const EditHoliday = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Dashboard/Holidays/HolidaysList/EditHoliday/EditHoliday'),
    'EditHoliday',
  ),
)
const NewBookingRoom = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/NewBooking/NewBooking'),
    'NewBookingRoom',
  ),
)
const EmployeeAllocation = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/AllocateEmployee/AllocateEmployee'),
    'EmployeeAllocation',
  ),
)
const AppraisalConfigurations = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/Configurations/AppraisalConfigurations'),
    'AppraisalConfigurations',
  ),
)
const EditConfiguration = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Settings/Configurations/EditConfiguration/EditConfiguration'
      ),
    'EditConfiguration',
  ),
)
const ViewPIPDetail = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Performance/PipList/EmployeePipTimeline/EmployeePipTimeline'
      ),
    'ViewPIPDetail',
  ),
)
const TicketConfiguration = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/TicketConfiguration/TicketConfiguration'),
    'TicketConfiguration',
  ),
)
const NewEvent = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/NewEvent/NewEvent'),
    'NewEvent',
  ),
)
const SubmitResignation = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Separation/SubmitViewResignation/SubmitResignation'),
    'SubmitResignation',
  ),
)
const LeaveApprovals = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Leaves/LeaveApprovals/LeaveApprovals'),
    'LeaveApprovals',
  ),
)
const AssignTemplate = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/Configurations/AssignTemplate'),
    'AssignTemplate',
  ),
)
const ITDeclarationForm = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/ITDeclarationForm/ITDeclarationForm'),
    'ITDeclarationForm',
  ),
)
const Payslip = React.lazy(() =>
  lazyRetry(() => import('./pages/Finance/Payslips/Payslips'), 'Payslip'),
)

const PfPanDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/PanAndBankDetails/PanAndBankDetails'),
    'PfPanDetails',
  ),
)
const ResignationTimeLine = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/ResignationTimeLine/ResignationHistory'
      ),
    'ResignationTimeLine',
  ),
)
const ResignationFeedBackForm = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/HRClearanceCertificate/ExitFeedBackForm/ExitFeedBackForm'
      ),
    'ResignationFeedBackForm',
  ),
)
const ReviewList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/ReviewList/ReviewList'),
    'ReviewList',
  ),
)
const ResignationManagerClearanceCertificate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/ManagerClearenceCertificate/ManagerClearanceCertificate'
      ),
    'ResignationManagerClearanceCertificate',
  ),
)
const SeparationViewChart = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/ViewChart/SeparationViewChart'
      ),
    'SeparationViewChart',
  ),
)
const ResignationHRClearanceCertificate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/HRClearanceCertificate/HRClearanceCertificate'
      ),
    'ResignationHRClearanceCertificate',
  ),
)
const ResignationITClearanceCertificate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/ITClearanceCertificate/ITClearanceCertificate'
      ),
    'ResignationITClearanceCertificate',
  ),
)
const ResignationFinanceClearanceCertificate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/FinanaceClearanceCertificate/FinanceClearanceCertificate'
      ),
    'ResignationFinanceClearanceCertificate',
  ),
)
const ResignationAdminClearanceCertificate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Separation/ResignationList/AdminClearanceCertificate/AdminClearanceCertificate'
      ),
    'ResignationAdminClearanceCertificate',
  ),
)
const ResignationList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Separation/ResignationList/ResignationList'),
    'ResignationList',
  ),
)
const EmployeeAccounts = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/EmployeeAccounts/EmployeeAccounts'),
    'EmployeeAccounts',
  ),
)
const ITDeclarationList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/ITDeclarationList/ITDeclarationList'),
    'ITDeclarationList',
  ),
)
const InvestmentCheckList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/InvestmentCheckList/InvestmentCheckList'),
    'InvestmentCheckList',
  ),
)
const AddInvestmentCycle = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/AddInvestmentCycle/AddInvestmentCycle'),
    'AddInvestmentCycle',
  ),
)
const AddInvestment = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/AddInvestment/InvestmentList'),
    'AddInvestment',
  ),
)
const AddSection = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/AddInvestment/Add Section/SectionsList'),
    'AddSection',
  ),
)
const ViewProjectDetails = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/ProjectViewLandingPage/ProjectView'
      ),
    'ViewProjectDetails',
  ),
)
const ProjectClosure = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/ProjectClosure/AddClosure/AddChecklist'
      ),
    'ProjectClosure',
  ),
)
const ViewProjectClosure = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/ProjectClosure/ViewClosure/ViewClosure'
      ),
    'ViewProjectClosure',
  ),
)
const EditProjectClosure = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/ProjectClosure/EditClosure/EditClosure'
      ),
    'EditProjectClosure',
  ),
)
const ClosureHistory = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/ProjectClosure/ClosureHistory/ProjectClosureTimeLine'
      ),
    'ClosureHistory',
  ),
)
const ProjectCreationRequest = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/ProjectCreationRequest/ProjectCreationRequest'
      ),
    'ProjectCreationRequest',
  ),
)
const AddAchiever = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Achievements/AddAchiever/AddAchiever'),
    'AddAchiever',
  ),
)
const AchieverList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Achievements/AchieverList/AchieverList'),
    'AchieverList',
  ),
)
const InitiateCycle = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/InitiateCycle/InitiateNewCycle'),
    'InitiateCycle',
  ),
)
const myKRAs = React.lazy(() =>
  lazyRetry(() => import('./pages/Performance/MyKRAs/MyKRAsList'), 'myKRAs'),
)
const NomineeList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Achievements/NomineeList/NomineeList'),
    'NomineeList',
  ),
)
const AddNominee = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Achievements/AddNominee/AddNominee'),
    'AddNominee',
  ),
)
const MyReview = React.lazy(() =>
  lazyRetry(() => import('./pages/Performance/MyReviews/MyReview'), 'MyReview'),
)
const MyReviewManagerAppraisal = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Performance/MyReviews/ManagerAppraisal/ManagerAppraisal'),
    'MyReviewManagerAppraisal',
  ),
)
const PayrollManagement = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/PayrollManagement/PayrollManagement'),
    'PayrollManagement',
  ),
)
const LeadershipEnrollmentList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Achievements/LeadershipEnrollmentList/LeadershipEnrollmentList'
      ),
    'LeadershipEnrollmentList',
  ),
)
const LeadershipEnrollmentForm = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Achievements/LeadershipEnrollmentForm/LeadershipEnrollmentForm'
      ),
    'LeadershipEnrollmentForm',
  ),
)
const PIPList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/PipList/EmployeePipList/PipList'),
    'PIPList',
  ),
)
const KRA = React.lazy(() =>
  lazyRetry(() => import('./pages/Performance/KRA/KRALandingScreen'), 'KRA'),
)
const MileStoneHistory = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/MileStone/MileStoneHistory/MileStoneHistory'
      ),
    'MileStoneHistory',
  ),
)
const InvoiceDiscussion = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/InvoicingDiscussion/invoicingDiscussion'
      ),
    'InvoiceDiscussion',
  ),
)
const InvoiceTimeline = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/InvoicingHistory/invoicingHistory'
      ),
    'InvoiceTimeline',
  ),
)
const MileStoneDiscussion = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/MileStone/MileStoneDiscussion/MileStoneDiscussion'
      ),
    'MileStoneDiscussion',
  ),
)
const AddAddress = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/AdderessList/AddAddress/AddAddressList'),
    'AddAddress',
  ),
)
const Adderesslist = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/AdderessList/AdderessList'),
    'Adderesslist',
  ),
)
const Billingreport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/ProjectMilestones/ProjectMilestone'),
    'Billingreport',
  ),
)
const EditBooking = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/EditBooking/EditBooking'),
    'EditBooking',
  ),
)
const ClearnceCerticates = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Performance/PipList/EmployeePIPClearenceCertificate/EmployeePIPClearenceCertificate'
      ),
    'ClearnceCerticates',
  ),
)
const EditEvent = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ConferenceRoomBooking/EditEvent/EditEvent'),
    'EditEvent',
  ),
)
const AppraisalTemplate = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/AppraisalTemplate/AppraisalTemplate'),
    'AppraisalTemplate',
  ),
)
const AddNewAudit = React.lazy(() =>
  lazyRetry(
    () => import('./pages/SQAAuditReport/AddNewAudit/AddNewAudit'),
    'AddNewAudit',
  ),
)
const SQAAudit = React.lazy(() =>
  lazyRetry(() => import('./pages/SQAAuditReport/SQAAuditReport'), 'SQAAudit'),
)
const EditAudit = React.lazy(() =>
  lazyRetry(
    () => import('./pages/SQAAuditReport/EditAuditForm/EditAudit'),
    'EditAudit',
  ),
)
const SQAAuditTimeLine = React.lazy(() =>
  lazyRetry(
    () => import('./pages/SQAAuditReport/SQAAuditTimeLine/SQAAuditHistory'),
    'SQAAuditTimeLine',
  ),
)
const SQAAuditViewReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/SQAAuditReport/SQAViewReport/SQAViewReport'),
    'SQAAuditViewReport',
  ),
)
const processAreaList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/ProcessArea/ProcessAreaMainFile'),
    'processAreaList',
  ),
)
const Notifications = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Notifications/Notifications'),
    'Notifications',
  ),
)
const JobVacancies = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/JobOpenings/JobOpenings'),
    'JobVacancies',
  ),
)
const AddJobVacancies = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/JobOpenings/AddJobOpening/AddJobOpening'),
    'AddJobVacancies',
  ),
)
const VendorList = React.lazy(() =>
  lazyRetry(() => import('./pages/Assets/VendorList/VendorList'), 'VendorList'),
)
const IntervieweeDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/IntervieweeDetails/IntervieweeDetails'),
    'IntervieweeDetails',
  ),
)
const CandidateList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/CandidateList/CandidateList'),
    'CandidateList',
  ),
)
const CandidateInterviewList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/CandidateList/CandidateInterviewList/CandidateInterviewList'
      ),
    'CandidateInterviewList',
  ),
)
const Manufacturer = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Assets/ManufacturerList/Manufacturer'),
    'Manufacturer',
  ),
)
const AssetWarrantyReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Assets/AssetWarrantyReport/AssetWarrantyReport'),
    'AssetWarrantyReport',
  ),
)
const productList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Assets/PrductTypeList/ProductTypeList'),
    'productList',
  ),
)
const ChangeReportees = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/ChangeReportees/ChangeReportees'),
    'ChangeReportees',
  ),
)
const ExpenseCategoryList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/Category/ExpenseCategoryList'),
    'ExpenseCategoryList',
  ),
)

const AssetList = React.lazy(() =>
  lazyRetry(() => import('./pages/Assets/AssetList/AssetList'), 'AssetList'),
)

const AssetTransactionalList = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Assets/AssetTransactionalList/AssetTransactionalList'),
    'AssetTransactionalList',
  ),
)

const ProductSpecificationList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Assets/ProductSpecificationList/ProductSpecificationList'
      ),
    'ProductSpecificationList',
  ),
)

const addMilestone = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/MileStone/AddMilestone/AddMilestone'
      ),
    'addMilestone',
  ),
)

const CreditCardList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/CreditCardList/CreditCardList'),
    'CreditCardList',
  ),
)

const ExpenseSubCategoryList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/Sub-Category/SubCategoryList'),
    'ExpenseSubCategoryList',
  ),
)

const ScheduleInterview = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/IntervieweeDetails/ScheduleIntervieActionButtons/Schedule'
      ),
    'ScheduleInterview',
  ),
)

const ReScheduleInterview = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/IntervieweeDetails/ScheduleIntervieActionButtons/ReSchedule'
      ),
    'ReScheduleInterview',
  ),
)

const InterviewStatusReport = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Recruitment/InterviewStatusReport/InterviewStatusReport'),
    'InterviewStatusReport',
  ),
)

const editMilestone = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/MileStone/EditMilestone/EditMilestone'
      ),
    'editMilestone',
  ),
)

const CompaniesList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/CompaniesList/CompaniesList'),
    'CompaniesList',
  ),
)

const AddNewCandidate = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Recruitment/CandidateList/AddCandidatePage/AddCandidate'),
    'AddNewCandidate',
  ),
)

const UpComingJoinList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/UpComingJoinList/UpComingJoinList'),
    'UpComingJoinList',
  ),
)

const ExpenseForm = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/ExpenseForm/ExpenseForm'),
    'ExpenseForm',
  ),
)

const CandidatesCount = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/CompaniesList/CandidatesCount'),
    'CandidatesCount',
  ),
)

const EmployeesCount = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/CompaniesList/EmployeesCount'),
    'EmployeesCount',
  ),
)

const JobInfo = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Recruitment/JobOpenings/JobInformation/JobInformation'),
    'JobInfo',
  ),
)

const AddNewEmployeeUnderJoinee = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/UpComingJoinList/AddNewEmployee/CreateEmployeeUnderJoinee'
      ),
    'AddNewEmployeeUnderJoinee',
  ),
)

const personTechnology = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Recruitment/UpComingJoinList/AddNewJoinee/AddNewJoinee'),
    'personTechnology',
  ),
)

const EditCandidate = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/CandidateList/EditCandidatePage/EditCandidate'
      ),
    'EditCandidate',
  ),
)

const PaymentList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/PaymentList/PaymentList'),
    'PaymentList',
  ),
)

const Checklist = React.lazy(() =>
  lazyRetry(() => import('./pages/Checklist/Checklist'), 'Checklist'),
)

const ChecklistInfo = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Checklist/ChecklistInfoPage/ChecklistInfo'),
    'ChecklistInfo',
  ),
)

const CandidateStatusReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/InterviewStatusReport/CandidateStatusReport/CandidateStatusReport'
      ),
    'CandidateStatusReport',
  ),
)

const ChecklistInfoSettings = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Checklist/ChecklistInfoSettings/ChecklistInfoSettings'),
    'ChecklistInfoSettings',
  ),
)

const Sections = React.lazy(() =>
  lazyRetry(() => import('./pages/Settings/Sections/Sections'), 'Sections'),
)

const PaymentForm = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/PaymentList/PaymentForm'),
    'PaymentForm',
  ),
)

const ExpensesList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/ExpenseList/ExpensesList'),
    'ExpensesList',
  ),
)

const ExpenseYearlyReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ExpenseManagement/ExpenseYearlyReport/ExpenseYearlyReport'
      ),
    'ExpenseYearlyReport',
  ),
)

const ExpensesListTimeLine = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ExpenseManagement/ExpenseList/ExpenseListTimeLine/ExpenseListHistory'
      ),
    'ExpensesListTimeLine',
  ),
)

const EditExpensesList = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ExpenseManagement/ExpenseList/EditExpensesList/EditExpensesList'
      ),
    'EditExpensesList',
  ),
)

const PaymentHistoryDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ExpenseManagement/PaymentList/PaymentHistoryDetails'),
    'PaymentHistoryDetails',
  ),
)

const RemittanceDetailsList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/RemittanceDetails/RemittanceDetailsList'),
    'RemittanceDetailsList',
  ),
)

const clientInvoiceReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/ClientInvoiceReport/ClientInvoiceReport'),
    'clientInvoiceReport',
  ),
)

const AddInvoicelocation = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/LocationList/AddInvoiceLocationList'),
    'AddInvoicelocation',
  ),
)

const AddBankList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/AddBankList/AddBankList'),
    'AddBankList',
  ),
)

const AddCurrencyList = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Invoicing/RemittanceDetails/AddCurrency/AddCurrencyList'),
    'AddCurrencyList',
  ),
)

const InvoiveRetainerPDF = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/InvoiceView/RetainerPDF'
      ),
    'InvoiveRetainerPDF',
  ),
)

const InvoiceFixedbidPDF = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/InvoiceView/FixedbidPDF'
      ),
    'InvoiceFixedbidPDF',
  ),
)

const AddTax = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/AddTax/AddTax'
      ),
    'AddTax',
  ),
)

const AddRemittanceDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/RemittanceDetails/AddRemittanceDetails'),
    'AddRemittanceDetails',
  ),
)

const AnniversaryListTable = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Dashboard/ServiceAnniversaries/ListOfServiceAnniversaries'
      ),
    'AnniversaryListTable',
  ),
)

const AddInvoiceForm = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/AddAndRaiseInvoice/AddInvoice'
      ),
    'AddInvoiceForm',
  ),
)

const AddCountry = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/AddCountry/AddCountry'
      ),
    'AddCountry',
  ),
)

const AddInvoiceAuditLog = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Invoicing/Invoices/InvoiceAuditLog/InvoiceAuditLogList'),
    'AddInvoiceAuditLog',
  ),
)

const InvoiceAuditReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/InvoiceAuditReport/InvoiceAuditReport'),
    'InvoiceAuditReport',
  ),
)

const InvoicesList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/Invoices/InvoicesList'),
    'InvoicesList',
  ),
)

const EditInvoice = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/EditInvoice/EditInvoice'
      ),
    'EditInvoice',
  ),
)

const InvoiceReport = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/InvoiceReport/InvoiceReport'),
    'InvoiceReport',
  ),
)

const InvoiceSummary = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/InvoiceSummary/InvoiceSummary'),
    'InvoiceSummary',
  ),
)

const InvoiceViewFixedBid = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Invoicing/Invoices/InvoicesView/FixedBidView/FixedBid'),
    'InvoiceViewFixedBid',
  ),
)

const InvoiveViewRetainer = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/Invoicing/Invoices/InvoicesView/RetainerView/Retainer'),
    'InvoiveViewRetainer',
  ),
)

const RaiseInvoiceProformaForm = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ProjectManagement/Project/ProjectView/Invoices/GenerateInvoice/GenerateInvoice'
      ),
    'RaiseInvoiceProformaForm',
  ),
)

const errorPage = React.lazy(() =>
  lazyRetry(() => import('./pages/ErrorPage/ErrorPage'), 'errorPage'),
)

const errorPagePolicy = React.lazy(() =>
  lazyRetry(() => import('./pages/ErrorPage/ErrorPolicy'), 'errorPagePolicy'),
)

const emptyPageRoute = React.lazy(() =>
  lazyRetry(() => import('./pages/EmptyPageReusable'), 'emptyPageRoute'),
)

const InvoiceViewVersions = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Invoicing/Invoices/InvoicesView/InvoiceViewVersions'),
    'InvoiceViewVersions',
  ),
)
const AccessCardDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/AccessCardList/AccessCardDetails'),
    'AccessCardDetails',
  ),
)
const InternDetails = React.lazy(() =>
  lazyRetry(
    () => import('./pages/EmployeeDirectory/InternDetails/InternDetails'),
    'InternDetails',
  ),
)

const InternEditDetails = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeDirectory/InternDetails/InternAddandEdit/InternDetailsEdit'
      ),
    'InternEditDetails',
  ),
)
const InternDetailsTimeline = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/EmployeeDirectory/InternDetails/internDetailsTimeline'),
    'InternDetailsTimeline',
  ),
)
const InternDetailsAdd = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeDirectory/InternDetails/InternAddandEdit/InternDetailsAdd'
      ),
    'InternDetailsAdd',
  ),
)
const Insurance = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/Insurance/InsuranceDetails/InsuranceDetails'),
    'Insurance',
  ),
)
const InsuranceDiscriptionPage = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/Insurance/InsuranceDetails/InsuranceView'),
    'InsuranceDiscriptionPage',
  ),
)
const AppraisalCard = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/AppraisalLetters/AppraisalLetters'),
    'AppraisalCard',
  ),
)
const InsuranceUpload = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/Insurance/InsuranceUpload/InsuranceUpload'),
    'Insurance',
  ),
)

const lateArrivalReport = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/TimeAndAttendance/LateArrivalReport/LateArrivalReport'),
    'Late Arrival Report',
  ),
)

const AttendanceRegularization = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/TimeAndAttendance/AttendanceRegularization/AttendanceRegularization'
      ),
    'AttendanceRegularization',
  ),
)

const ResumeMatcher = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/ResumeMatcher/ResumeMatcher'),
    'ResumeMatcher',
  ),
)

const ResumeMatcherView = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/ResumeMatcher/ResumeMatcherView'),
    'ResumeMatcherView',
  ),
)

const PmoConfiguration = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ProjectManagement/PmoConfiguration/PmoConfiguration'),
    'PmoConfiguration',
  ),
)
const FormSixteenDownload = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/FormSixteenDownload/FormSixteenDownload'),
    'FormSixteenDownload',
  ),
)
const FormSixteenUpload = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/FormSixteenUpload/FormSixteenUpload'),
    'FormSixteenUpload',
  ),
)
const ComplianceList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ComplianceReminder/ComplianceReminderFilterOption'),
    'ComplianceReminder',
  ),
)
const TimeLineHistoryTask = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/ComplianceReminder/ComplianceReminderTimeline/ComplianceReminderHistory'
      ),
    'TimeLineHistory',
  ),
)
const AddNewCompliance = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/ComplianceReminder/AddNewCompliance/AddNewCompliance'),
    'AddNewCompliance',
  ),
)
const EditCompliance = React.lazy(() =>
  lazyRetry(
    () => import('./pages/ComplianceReminder/EditCompliance/EditCompliance'),
    'EditCompliance',
  ),
)
const AddNewHandbook = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeHandbook/HandbookSettings/AddNewPage/AddNewHandbook'
      ),
    'AddNewHandbook',
  ),
)
const EmployeeHandbookPolicyReport = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeHandbook/HandbookSettings/EmployeeHandbookPolicyReport/EmployeeHandbookPolicyReport'
      ),
    'EmployeeHandbookPolicyReport',
  ),
)
const EditPolicyMessage = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/EmployeeHandbook/HandbookSettings/EditPolicyMessage/EditPolicyMessage'
      ),
    'EditPolicyMessage',
  ),
)
const EditHandbookPage = React.lazy(() =>
  lazyRetry(
    () =>
      import('./pages/EmployeeHandbook/HandbookSettings/EditPage/EditHandbook'),
    'EditHandbookPage',
  ),
)
const InterviewerList = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Recruitment/InterviewerList/InterviewerList'),
    'interviewerList',
  ),
)

const InterviewerConfiguration = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/InterviewerList/InterviewerConfiguration/AddInterviewerConfiguration'
      ),
    'interviewerConfiguration',
  ),
)

const EditInterviewerConfiguration = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        './pages/Recruitment/InterviewerList/InterviewerConfiguration/EditInterviewerConfiguration'
      ),
    'editInterviewerConfiguration',
  ),
)
const Ovservation = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/AddObservation/Observation'),
    'Observation',
  ),
)
const ObservationTable = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/AddObservation/ObservationTable'),
    'ObservationTable',
  ),
)
const EditObservation = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Performance/AddObservation/Edit/EditObservationForm'),
    'EditObservation',
  ),
)
const TaxCalculator = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Finance/Taxcalculator/Taxcalculator'),
    'TaxCalculator',
  ),
)

const UploadFestivalImage = React.lazy(() =>
  lazyRetry(
    () => import('./pages/Settings/FestivalManagement/FestivalUpload'),
    'UploadFestivalImage',
  ),
)

/**
 * See {@link https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config GitHub}.
 */
const routes = [
  { path: '/', exact: true, name: 'Home' },
  { path: '/dashboard', name: 'Dashboard', component: Dashboard },
  {
    path: '/roleslist',
    name: 'User Roles And Permission',
    component: UserRolesAndPermissions,
  },
  { path: '/profile', name: 'My Profile', component: MyProfile },
  {
    path: '/employeeCertificatesReport',
    name: 'Employee Certificates Report',
    component: CertificatesList,
  },
  {
    path: '/certificateTypeList',
    name: 'Certificate Type List',
    component: CertificateTypeList,
  },
  { path: '/employeeList', name: 'Employee List', component: EmployeeList },
  {
    path: '/empReport',
    name: 'Employee Report',
    component: EmployeeReport,
  },
  {
    path: '/report2',
    name: 'Employee Designation List Report',
    component: EmpDesignationReport,
  },
  {
    path: '/ticketReport',
    name: 'Support Report',
    component: SupportReport,
  },
  {
    path: '/employeeProfile/:employeeId',
    name: 'Employee Profile',
    component: MyProfile,
  },
  {
    path: '/visaDetailsList',
    name: 'Visa Details',
    component: VisaDetailsList,
  },
  {
    path: '/employeehandbook',
    name: 'Employee Handbook',
    component: EmployeeHandbook,
  },
  {
    path: '/Employeereport',
    name: 'Employee Allocation Report',
    component: EmployeeAllocationReport,
  },
  {
    path: '/leaveReports',
    name: 'Leave Reports',
    component: LeaveReports,
  },
  {
    path: '/showemployeehandbook/:clickedpageName',
    name: 'Handbook',
    component: Handbook,
  },
  {
    path: '/attendancesummary',
    name: 'Attendance Summary',
    component: AttendanceReport,
  },

  {
    path: '/shortfallTrackerReport',
    name: 'Shortfall Tracker Report',
    component: ShortfallTrackerReport,
  },
  {
    path: '/createTicket',
    name: 'Raise Ticket',
    component: CreateNewTicket,
  },
  {
    path: '/handbooksettings',
    name: 'Handbook Settings',
    component: EmployeeHandbookSettings,
  },
  {
    path: '/addNewHandbook',
    name: 'Add New Handbook',
    component: AddNewHandbook,
  },
  {
    path: '/editPolicyMessage',
    name: 'Edit Policy Message',
    component: EditPolicyMessage,
  },
  {
    path: '/editHandbook/:handbookId',
    name: 'Edit Handbook',
    component: EditHandbookPage,
  },
  {
    path: '/employeeHandbookPolicyReport',
    name: 'Employee Handbook Policy Report',
    component: EmployeeHandbookPolicyReport,
  },
  {
    path: '/timeInOfficeReport',
    name: 'Time in Office Report',
    component: TimeInOfficeReport,
  },
  {
    path: '/vpnAttendance',
    name: 'VPN Attendance',
    component: VpnAttendance,
  },
  {
    path: '/leaveSettings',
    name: 'Leave Settings',
    component: LeaveSettings,
  },
  {
    path: '/addNewEmployee',
    name: 'Add New Employee',
    component: AddNewEmployee,
  },
  {
    path: '/addNewIntern/:internId',
    name: 'Employee Documents',
    component: AddNewEmployee,
  },
  {
    path: '/mailTemplates',
    name: 'Mail Configuration',
    component: MailConfiguration,
  },
  {
    path: '/hiveReport',
    name: 'Hive Activity Report',
    component: HiveActivityReport,
  },
  {
    path: '/addTemplate',
    name: 'Add Template',
    component: AddNewTemplate,
  },
  {
    path: '/editEmployee/:employeeId',
    name: 'Edit Employee',
    component: EditEmployee,
  },
  {
    path: '/mailTemplates',
    name: 'Mail Configuration',
    component: MailConfiguration,
  },
  {
    path: '/leaveApplication',
    name: 'Leaves',
    component: ApplyLeave,
  },
  {
    path: '/candidateSearch',
    name: 'Scheduled Interviews',
    component: ScheduledInterview,
  },
  {
    path: '/employeeattendance',
    name: 'Employee Attendance',
    component: MyAttendance,
  },
  {
    path: '/addproject',
    name: 'Add Project',
    component: AddProject,
  },
  {
    path: '/allocationEmployee',
    name: 'Allocate Employee',
    component: EmployeeAllocation,
  },
  {
    path: '/editproject/:projectId',
    name: 'Edit Project',
    component: EditProject,
  },
  {
    path: '/clientsList',
    name: 'Clients',
    component: Clients,
  },
  {
    path: '/addClient',
    name: 'Add Client',
    component: AddClient,
  },
  {
    path: '/ticketSummary',
    name: 'Tickets',
    component: MyTickets,
  },
  {
    path: '/clientInfo/:clientId',
    name: 'View Client',
    component: ViewClient,
  },
  {
    path: '/editClient/:clientId',
    name: 'Edit Client',
    component: EditClient,
  },
  {
    path: '/employeeLeaveSummary',
    name: 'Leave Summary',
    component: LeaveSummary,
  },
  {
    path: '/ticketApprovals',
    name: 'Ticket Approvals',
    component: TicketApprovals,
  },
  {
    path: '/updateTicketInApprovals/:ticketId',
    name: 'Update Ticket',
    component: UpdateTicket,
  },

  {
    path: '/updateTicket/:ticketId',
    name: 'Update Ticket',
    component: UpdateTicketUnderTicketList,
  },
  // {
  //   path: '/jobvacancies',
  //   name: 'Job Openings',
  //   component: JobOpenings,
  // },
  {
    path: '/birthdaylist',
    name: 'List of Birthdays',
    component: BirthdaysListTable,
  },
  {
    path: '/holidaylist',
    name: 'List of Holidays',
    component: HolidaysList,
  },
  {
    path: '/addHoliday',
    name: 'Add Holiday',
    component: AddHoliday,
  },
  {
    path: '/eventList',
    name: 'Event List',
    component: EventsList,
  },
  {
    path: '/probationaryList',
    name: 'Upcoming Probationary End Dates',
    component: ProbationaryList,
  },
  {
    path: '/OnContractList',
    name: 'Upcoming On Contract End Dates',
    component: OnContractList,
  },
  {
    path: '/meetingList',
    name: 'Booking List',
    component: BookingList,
  },
  {
    path: '/newEvent',
    name: 'New Event',
    component: NewEvent,
  },
  {
    path: '/addEventType',
    name: 'Event Type List',
    component: EventTypeList,
  },
  // {
  //   path: '/newMeetingRequest',
  //   name: 'Add LocationList',
  //   component: LocationList,
  // },
  {
    path: '/trainingFeedBackForm/:eventId',
    name: 'Feedback Forms',
    component: FeedbackForms,
  },
  {
    path: '/editHoliday/:holidayId',
    name: 'Edit Holiday',
    component: EditHoliday,
  },
  {
    path: '/newMeetingRequest',
    name: 'New Booking',
    component: NewBookingRoom,
  },
  {
    path: '/appraisalCycle',
    name: 'Appraisal Configurations',
    component: AppraisalConfigurations,
  },
  // {
  //   path: '/roomList',
  //   name: 'RoomList',
  //   component: RoomList,
  // },
  {
    path: '/editAppraisalCycle/:cycleId',
    name: 'Edit Configuration',
    component: EditConfiguration,
  },
  {
    path: '/subCategoryList',
    name: 'Ticket Configuration',
    component: TicketConfiguration,
  },
  {
    path: '/assignTemplate/:cycleId',
    name: 'Assign Template',
    component: AssignTemplate,
  },
  {
    path: '/submitResignation',
    name: 'Submit View Resignation',
    component: SubmitResignation,
  },
  {
    path: '/projectreport',
    name: 'Project Report',
    component: ProjectReport,
  },
  {
    path: '/leaveApprovals',
    name: 'Leave Approvals',
    component: LeaveApprovals,
  },
  {
    path: '/itDeclarationForm',
    name: 'IT Declaration Form',
    component: ITDeclarationForm,
  },
  {
    path: '/payslip',
    name: 'Payslip',
    component: Payslip,
  },
  {
    path: '/myFinance',
    name: 'P.F. & PAN Details',
    component: PfPanDetails,
  },
  {
    path: '/employeeFinance/:employeeId',
    name: 'P.F. & PAN Details',
    component: PfPanDetails,
  },
  {
    path: '/resignationList',
    name: 'Resignation List',
    component: ResignationList,
  },
  {
    path: '/managerComments/:separationId',
    name: 'Resignation Time Line',
    component: ResignationTimeLine,
  },
  {
    path: '/financeList',
    name: 'Employee Accounts',
    component: EmployeeAccounts,
  },
  {
    path: '/ClearanceCertificateManager/:separationId',
    name: 'Resignation Clearence CertificateManager',
    component: ResignationManagerClearanceCertificate,
  },
  {
    path: '/itDeclarationList',
    name: 'IT Declaration List',
    component: ITDeclarationList,
  },
  {
    path: '/addCycle',
    name: 'Add Investment Cycle',
    component: AddInvestmentCycle,
  },
  {
    path: '/addInvestment',
    name: 'Add Investment',
    component: AddInvestment,
  },
  {
    path: '/ClearanceCertificateHR/:separationId',
    name: 'HR  Clearance Certificate',
    component: ResignationHRClearanceCertificate,
  },

  {
    path: '/ClearanceCertificateIT/:separationId',
    name: 'IT  Clearance Certificate',
    component: ResignationITClearanceCertificate,
  },
  {
    path: '/ClearanceCertificateFinance/:separationId',
    name: 'Finance  Clearance Certificate',
    component: ResignationFinanceClearanceCertificate,
  },
  {
    path: '/ClearanceCertificateAdmin/:separationId',
    name: 'Admin  Clearance Certificate',
    component: ResignationAdminClearanceCertificate,
  },

  {
    path: '/ExitFeedBackForm',
    name: 'Resignation ExitFeedBackForm',
    component: ResignationFeedBackForm,
  },
  {
    path: '/investmentCheckList',
    name: 'Investment CheckList',
    component: InvestmentCheckList,
  },
  {
    path: '/insuranceDiscription',
    name: 'InsuranceDiscriptionPage',
    component: InsuranceDiscriptionPage,
  },
  {
    path: '/payslipUpload',
    name: 'Payroll Management',
    component: PayrollManagement,
  },
  {
    path: '/achievementList',
    name: 'Achievements List',
    component: AchieverList,
  },
  {
    path: '/separationChart',
    name: 'Separation View Chart',
    component: SeparationViewChart,
  },
  {
    path: '/addSection',
    name: 'Add Section',
    component: AddSection,
  },
  {
    path: '/projectRequestList',
    name: 'Project Creation Request',
    component: ProjectCreationRequest,
  },
  {
    path: '/addAchievement',
    name: 'Add Achiever',
    component: AddAchiever,
  },
  {
    path: '/viewProject/:projectId',
    name: 'View Project',
    component: ViewProjectDetails,
  },
  {
    path: '/projectClosure/:projectId',
    name: 'ProjectClosure',
    component: ProjectClosure,
  },
  {
    path: '/viewProjectClosure/:projectId/:id',
    name: 'ViewProjectClosure',
    component: ViewProjectClosure,
  },
  {
    path: '/editProjectClosure/:projectId/:id',
    name: 'EditProjectClosure',
    component: EditProjectClosure,
  },
  {
    path: '/closureHistory/:id',
    name: 'ClosureHistory',
    component: ClosureHistory,
  },
  {
    path: '/initiateCycle',
    name: 'Initiate Cycle',
    component: InitiateCycle,
  },
  {
    path: '/myKRAs',
    name: 'My KRAs',
    component: myKRAs,
  },
  {
    path: '/nomineesList',
    name: 'Nominee List',
    component: NomineeList,
  },
  {
    path: '/addNominee',
    name: 'Add Nominee',
    component: AddNominee,
  },
  {
    path: '/employeeAppraisal',
    name: 'My Review',
    component: MyReview,
  },
  {
    path: '/managerAppraisal/:employeeId/:appraisalFormId',
    name: 'Manager Appraisal',
    component: MyReviewManagerAppraisal,
  },
  {
    path: '/leadershiplist',
    name: 'Leadership Enrollment List',
    component: LeadershipEnrollmentList,
  },
  {
    path: '/leadershipForm',
    name: 'Leadership Enrollment Form',
    component: LeadershipEnrollmentForm,
  },
  {
    path: '/PIPList',
    name: 'PIP List',
    component: PIPList,
  },
  {
    path: '/addKra',
    name: 'KRA',
    component: KRA,
  },
  {
    path: '/ViewPIPDetail/:id',
    name: 'ViewPIPDetail',
    component: ViewPIPDetail,
  },
  {
    path: '/PIPClearnceCerticates',
    name: 'ClearnceCerticates',
    component: ClearnceCerticates,
  },
  {
    path: '/listofAppraisal',
    name: 'KRA',
    component: ReviewList,
  },

  {
    path: '/milestonehistory/:projectId',
    name: 'MileStone History',
    component: MileStoneHistory,
  },
  {
    path: '/milestoneNewsFeed/:id/:projectId',
    name: 'MileStone Discussion',
    component: MileStoneDiscussion,
  },
  {
    path: '/invoiceDiscussion/:invoiceId/:projectId',
    name: 'InvoiceDiscussion',
    component: InvoiceDiscussion,
  },
  {
    path: '/AuditPage/:InvoiceId',
    name: 'invoiceHistory',
    component: InvoiceTimeline,
  },
  {
    path: '/editEvent/:eventId',
    name: 'Edit Event',
    component: EditEvent,
  },
  {
    path: '/MeetingRequestEdit/:id',
    name: 'Edit Booking',
    component: EditBooking,
  },
  {
    path: '/appraisalTemplate',
    name: 'appraisalTemplate',
    component: AppraisalTemplate,
  },
  {
    path: '/processAreaList',
    name: 'processAreaList',
    component: processAreaList,
  },
  {
    path: '/addAuditForm',
    name: 'Add New Audit',
    component: AddNewAudit,
  },
  {
    path: '/SQAAudit',
    name: '/SQA Audit',
    component: SQAAudit,
  },
  {
    path: '/editAuditForm/:auditId',
    name: 'Edit Audit Form',
    component: EditAudit,
  },
  {
    path: '/newProjectAuditTimeline/:auditId',
    name: 'SQA Audit TimeLine',
    component: SQAAuditTimeLine,
  },
  {
    path: '/viewProjectAudit/:auditId',
    name: 'SQA Audit View',
    component: SQAAuditViewReport,
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: Notifications,
  },
  {
    path: '/jobvacancies',
    name: 'Jobvacancies',
    component: JobVacancies,
  },
  {
    path: '/addJobvacancies',
    name: 'AddJobVacancies',
    component: AddJobVacancies,
  },
  {
    path: '/resumematcherview',
    name: 'Resume Matcher View',
    component: ResumeMatcherView,
  },
  {
    path: '/candidatetimeline/:personId/:intervwcycleId',
    name: 'Interviewee Details',
    component: IntervieweeDetails,
  },
  {
    path: '/jobschedulecandidateList',
    name: 'Candidate List',
    component: CandidateList,
  },
  {
    path: '/candidateInterviewList',
    name: 'Candidate Interview List',
    component: CandidateInterviewList,
  },
  {
    path: '/candidatetimeline/:personId',
    name: 'Interviewee Details',
    component: IntervieweeDetails,
  },
  {
    path: '/vendorList',
    name: 'Vendor List',
    component: VendorList,
  },
  {
    path: '/manufacturerList',
    name: 'manufacturerList',
    component: Manufacturer,
  },
  {
    path: '/productList',
    name: 'Product List',
    component: productList,
  },
  {
    path: '/delegation',
    name: 'Change Reportees',
    component: ChangeReportees,
  },
  {
    path: '/assetWarrantyReport',
    name: 'AssetWarrantyReport',
    component: AssetWarrantyReport,
  },
  {
    path: '/expenseCategory',
    name: 'Expense Category',
    component: ExpenseCategoryList,
  },
  {
    path: '/assetList',
    name: 'Asset List',
    component: AssetList,
  },
  {
    path: '/transactionalList',
    name: 'AssetTransactionalList',
    component: AssetTransactionalList,
  },
  {
    path: '/productSpecificationList',
    name: 'Product Specification List',
    component: ProductSpecificationList,
  },
  {
    path: '/creditCardList',
    name: 'Credit Card List',
    component: CreditCardList,
  },
  {
    path: '/expenseSubCategory',
    name: 'Expense Sub Category',
    component: ExpenseSubCategoryList,
  },
  {
    path: '/scheduleInterview/:personId',
    name: 'schedule Interview',
    component: ScheduleInterview,
  },
  {
    path: '/reScheduleInterview/:personId',
    name: 'schedule Interview',
    component: ReScheduleInterview,
  },
  {
    path: '/interviewstatusReport',
    name: 'Interview Status Report',
    component: InterviewStatusReport,
  },
  {
    path: '/companiesList',
    name: 'Companies List',
    component: CompaniesList,
  },
  {
    path: '/addNewCandidate',
    name: 'Add New Candidate',
    component: AddNewCandidate,
  },
  {
    path: '/upcomingjoinlist',
    name: 'UpComing Join List',
    component: UpComingJoinList,
  },
  {
    path: '/expenseForm',
    name: 'Expense Form',
    component: ExpenseForm,
  },
  {
    path: '/candidatesInfo/:companyName',
    name: 'CandidatesCount',
    component: CandidatesCount,
  },
  {
    path: '/employeesInfo/:companyName',
    name: 'EmployeesCount',
    component: EmployeesCount,
  },
  {
    path: '/jobinfo/:jobVacancyId',
    name: 'JobInfo',
    component: JobInfo,
  },
  {
    path: '/addnewjoinee/:personId',
    name: 'CandidateOffer',
    component: personTechnology,
  },
  {
    path: '/editcandidate/:personId',
    name: 'Edit Candidate',
    component: EditCandidate,
  },
  {
    path: '/paymentList',
    name: 'Payment List',
    component: PaymentList,
  },
  {
    path: '/paymentForm/:expenseId',
    name: 'Payment Form',
    component: PaymentForm,
  },
  {
    path: '/paymentTimeline/:expenseId',
    name: 'Payment History Details',
    component: PaymentHistoryDetails,
  },
  {
    path: '/addEmployee/:joineeId',
    name: 'AddNewEmployeeUnderJoinee',
    component: AddNewEmployeeUnderJoinee,
  },
  { path: '/Checklist', name: 'Checklist', component: Checklist },
  {
    path: '/checklistInfo/:pageName',
    name: 'Checklist Info',
    component: ChecklistInfo,
  },
  {
    path: '/checklistInfosettings',
    name: 'Checklist Info Settings',
    component: ChecklistInfoSettings,
  },
  {
    path: '/statusChart',
    name: 'Candidate Status Report',
    component: CandidateStatusReport,
  },

  {
    path: '/expenseYearlyReport',
    name: '/Expenses Yearly List',
    component: ExpenseYearlyReport,
  },
  {
    path: '/expensesList',
    name: 'Expenses List',
    component: ExpensesList,
  },
  {
    path: '/expenseTimeline/:formId',
    name: 'Expense List TimeLine',
    component: ExpensesListTimeLine,
  },
  {
    path: '/editExpenses/:expenseId',
    name: 'Edit Expense List',
    component: EditExpensesList,
  },
  {
    path: '/retainerMilestone/:projectId',
    name: 'Add mileStone',
    component: addMilestone,
  },
  {
    path: '/fixedBidMilestone/:projectId',
    name: 'Add mileStone',
    component: addMilestone,
  },
  {
    path: '/editRetainerMilestone/:milestoneId/:projectId',
    name: 'Edit mileStone',
    component: editMilestone,
  },
  {
    path: '/editFixedBidMilestone/:milestoneId/:projectId',
    name: 'Edit mileStone',
    component: editMilestone,
  },
  {
    path: '/sections',
    name: '/sections',
    component: Sections,
  },
  {
    path: '/remittanceDetailsList',
    name: 'RemittanceDetailsList',
    component: RemittanceDetailsList,
  },
  {
    path: '/addRemittance',
    name: 'addRemittance',
    component: AddRemittanceDetails,
  },
  {
    path: '/addressList',
    name: 'AdderessList',
    component: Adderesslist,
  },
  {
    path: '/billingreport',
    name: 'Billingreport',
    component: Billingreport,
  },
  {
    path: '/addAddress',
    name: 'AddAddress',
    component: AddAddress,
  },
  {
    path: '/clientInvoiceReport',
    name: 'client Invoice Report',
    component: clientInvoiceReport,
  },
  {
    path: '/addLocationForRemmittance',
    name: 'addLocation',
    component: AddInvoicelocation,
  },
  {
    path: '/addBankForRemmittance',
    name: '/addBankList',
    component: AddBankList,
  },

  {
    path: '/addCurrencyForRemmittance',
    name: '/addCurrencyList',
    component: AddCurrencyList,
  },
  {
    path: '/AddTax/:projectId',
    name: '/AddTax',
    component: AddTax,
  },
  {
    path: '/getRetainerPDF',
    name: '/RetainerPDF',
    component: InvoiveRetainerPDF,
  },
  {
    path: '/getFixedbidPDF',
    name: '/FixedbidPDF',
    component: InvoiceFixedbidPDF,
  },

  {
    path: '/addCountry/:projectId',
    name: '/addCountry',
    component: AddCountry,
  },
  {
    path: '/anniversariesList',
    name: 'List of Anniversaries',
    component: AnniversaryListTable,
  },
  {
    path: '/addRetainerInvoice/:milestoneId/:projectId',
    name: '/addRetainerInvoice',
    component: AddInvoiceForm,
  },
  {
    path: '/addInvoice/:milestoneId/:projectId',
    name: '/addRetainerInvoice',
    component: AddInvoiceForm,
  },
  {
    path: '/invoiceAuditLog',
    name: 'addInvoiceAuditLog',
    component: AddInvoiceAuditLog,
  },
  {
    path: '/InvoicesAuditList',
    name: 'InvoicesAuditList',
    component: InvoiceAuditReport,
  },
  {
    path: '/InvoicesList',
    name: 'InvoicesList',
    component: InvoicesList,
  },
  {
    path: '/invoiceSummary',
    name: 'invoiceSummary',
    component: InvoiceSummary,
  },
  {
    path: '/editInvoice/:invoiceId/:projectId',
    name: 'EditInvoice',
    component: EditInvoice,
  },
  {
    path: '/editRetainerInvoice/:invoiceId/:projectId',
    name: 'EditInvoice',
    component: EditInvoice,
  },
  {
    path: '/yearlyInvoiceReport',
    name: 'InvoicesReportList',
    component: InvoiceReport,
  },
  {
    path: '/raiseProformaInvoice/:invoiceId/:projectId',
    name: 'RaiseInvoiceProformaForm',
    component: RaiseInvoiceProformaForm,
  },
  {
    path: '/getFixedBidPreview',
    name: 'GetFixedBidPreview',
    component: InvoiceViewFixedBid,
  },
  {
    path: '/getRetainerPreview',
    name: 'GetRetainerPreview',
    component: InvoiveViewRetainer,
  },
  {
    path: '/invoiceViewVersions',
    name: 'InvoiceViewVersions',
    component: InvoiceViewVersions,
  },
  {
    path: '/forbidden',
    name: 'forbiddenPage',
    component: errorPage,
  },
  {
    path: '/forbiddenPolicy',
    name: 'forbiddenPagePolicy',
    component: errorPagePolicy,
  },

  {
    path: '/reporteesEmpProfile/:employeeProfileId',
    name: 'Employee Profile View',
    component: MyProfile,
  },
  {
    path: '/internsList',
    name: 'internsList',
    component: InternDetails,
  },
  {
    path: '/accessCardList',
    name: 'Access Card List',
    component: AccessCardDetails,
  },
  {
    path: '/editInternDetails/:internId',
    name: 'InternEditDetails',
    component: InternEditDetails,
  },

  {
    path: '/internsTimeline/:internId',
    name: 'InternsTimeline',
    component: InternDetailsTimeline,
  },

  {
    path: '/addNewIntern',
    name: 'Add New Intern',
    component: InternDetailsAdd,
  },
  {
    path: '/insuranceDownload',
    name: 'Insurance',
    component: Insurance,
  },
  {
    path: '/appraisalDownload',
    name: 'AppraisalCard',
    component: AppraisalCard,
  },
  {
    path: '/insuranceUpload',
    name: 'Insurance Upload',
    component: InsuranceUpload,
  },
  {
    path: '/lateArrivalReport',
    name: 'Late Arrival Report',
    component: lateArrivalReport,
  },
  {
    path: '/attendanceRegularization',
    name: 'AttendanceRegularization',
    component: AttendanceRegularization,
  },
  {
    path: '/resumeMatcher',
    name: 'Resume Matcher',
    component: ResumeMatcher,
  },
  {
    path: '/resume-matcher-view/:jobVacancy?',
    name: 'Resume Matcher View',
    component: ResumeMatcherView,
  },
  {
    path: '/resumeMatcher',
    name: 'Resume Matcher',
    component: ResumeMatcher,
  },
  {
    path: '/PmoConfiguration',
    name: 'PmoConfiguration',
    component: PmoConfiguration,
  },
  {
    path: '/DownloadForm16',
    name: 'FormSixteenDownload',
    component: FormSixteenDownload,
  },

  {
    path: '/UploadForm16',
    name: 'FormSixteenUpload',
    component: FormSixteenUpload,
  },
  {
    path: '/insuranceDiscription',
    name: 'InsuranceDiscriptionPage',
    component: InsuranceDiscriptionPage,
  },
  {
    path: '/ComplianceReminder',
    name: 'ComplianceReminder',
    component: ComplianceList,
  },
  {
    path: '/HistoryCompliance/:id',
    name: 'ComplianceReminder',
    component: TimeLineHistoryTask,
  },
  {
    path: '/AddNewCompliance',
    name: 'ComplianceReminder',
    component: AddNewCompliance,
  },
  {
    path: '/EditCompliance/:id',
    name: 'ComplianceReminder',
    component: EditCompliance,
  },
  {
    path: '/interviewerList',
    name: 'interviewerList',
    component: InterviewerList,
  },

  {
    path: '/interviewerConfiguration',
    name: 'interviewerConfiguration',
    component: InterviewerConfiguration,
  },
  {
    path: '/editInterviewerConfiguration/:interviewerId',
    name: 'editInterviewerConfiguration',
    component: EditInterviewerConfiguration,
  },
  {
    path: '/addObservations',
    name: 'Add Observations',
    component: Ovservation,
  },
  {
    path: '/employeeObservation',
    name: 'Employee Observations',
    component: ObservationTable,
  },
  {
    path: '/editObservation/:observationId',
    name: 'Edit Observation',
    component: EditObservation,
  },
  {
    path: '/taxCalculator',
    name: 'Tax Calculator',
    component: TaxCalculator,
  },
  {
    path: '/uploadFestivalImage',
    name: 'UploadFestivalImage',
    component: UploadFestivalImage,
  },
  {
    path: '/',
    name: 'emptyPageRoute',
    component: emptyPageRoute,
  },
]
export default routes
